import React, { useCallback, useState } from 'react'
import { Box, Typography, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'

export interface UploadDropzoneProps {
  onFiles: (files: File[]) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  progress?: number | null
}

const Zone = styled(Box, {
  shouldForwardProp: (p) => p !== 'active' && p !== 'disabled',
})<{ active?: boolean; disabled?: boolean }>(({ active, disabled }) => ({
  borderRadius: 'var(--ds-radius-xl)',
  border: `2px dashed ${active ? 'var(--ds-ink)' : 'color-mix(in srgb, var(--ds-ink) 28%, transparent)'}`,
  backgroundColor: active
    ? 'var(--ds-surface)'
    : 'color-mix(in srgb, var(--ds-surface) 88%, transparent)',
  padding: '28px var(--ds-space-5)',
  textAlign: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
  boxShadow: active ? 'var(--ds-shadow-soft)' : 'none',
  transition: [
    `border-color var(--ds-duration-fast) var(--ds-ease)`,
    `background var(--ds-duration-fast) var(--ds-ease)`,
    `box-shadow var(--ds-duration-normal) var(--ds-ease)`,
  ].join(', '),
}))

export function UploadDropzone({
  onFiles,
  accept = 'image/*,.heic,.heif',
  multiple = true,
  disabled,
  progress = null,
}: UploadDropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [active, setActive] = useState(false)

  const take = useCallback(
    (list: FileList | null) => {
      if (!list?.length || disabled) return
      onFiles(Array.from(list))
    },
    [onFiles, disabled],
  )

  return (
    <Zone
      active={active}
      disabled={disabled}
      role='button'
      aria-label='Add photos — drop images or click to browse'
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setActive(true)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setActive(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setActive(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setActive(false)
        take(e.dataTransfer.files)
      }}
    >
      <input
        ref={inputRef}
        type='file'
        hidden
        accept={accept}
        multiple={multiple}
        onChange={(e) => take(e.target.files)}
      />
      <CloudUploadOutlinedIcon
        sx={{ fontSize: 28, color: 'var(--ds-ink-muted)', mb: 1.25, display: 'block', mx: 'auto' }}
      />
      <Typography
        sx={{
          fontFamily: 'var(--ds-font-display)',
          fontWeight: 700,
          fontSize: 18,
          letterSpacing: '-0.03em',
          mb: 0.75,
          color: 'var(--ds-ink)',
        }}
      >
        Add photos
      </Typography>
      <Typography sx={{ color: 'var(--ds-ink-muted)', fontSize: 14, lineHeight: 1.45 }}>
        Drop images here or click to browse
      </Typography>
      {progress != null && (
        <Box mt={3} px={4}>
          <LinearProgress variant='determinate' value={progress} />
        </Box>
      )}
    </Zone>
  )
}
